/**
 * Chat 域 — API 接口
 * 
 * 对接后端 assistant/handler.rs 的 Chat SSE 流式聊天
 * 
 * 后端路由：
 * - POST /chat                              → SSE 流式聊天（独立消息）
 * - GET/POST /chat/sessions                  → 会话 CRUD
 * - GET /chat/sessions/{id}/messages         → 消息历史
 * - POST /chat/sessions/{id}/messages        → 发送消息（非流式）
 * - POST /chat/sessions/{id}/messages/stream → SSE 流式聊天（会话内，自动加载历史+健康档案）
 * - DELETE /chat/sessions/{id}               → 删除会话
 * - GET /chat/latest                         → 最近会话预览
 */

import { http } from '../../api/http';
import { API_BASE, shouldUseMock } from '../../api/config';
import { createSSEStream, type SSEEvent } from '../../api/sse';
import { useSessionStore } from '../../stores/session-store';

// ── 类型定义 ──

export interface ChatSession {
  id: string;
  title: string;
  lastMessageAt?: string;
  status: string;
  contextCourseId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface Attachment {
  attachmentId?: string;
  url: string;
  mimeType: string;
  fileName: string;
  sizeBytes?: number;
  /** @deprecated Internal OSS refs must not be stored or sent by clients. */
  objectRef?: string;
  mediaKind?: 'image' | 'pdf' | 'file';
  uploadStatus?: 'selected' | 'uploading' | 'uploaded' | 'upload_failed';
  dedupeKey?: string;
  groupId?: string;
  fileOrder?: number;
}

export interface ThinkingProgress {
  status: string;
  phase?: string;
  step?: string;
  progress?: number;
}

export interface AgentEvidenceCard {
  kind?: string | null;
  title?: string | null;
  summary?: string | null;
  date?: string | null;
  source?: string | null;
  referenceRange?: string | null;
  priority?: boolean;
}

export interface AgentRunStats {
  route?: string | null;
  invocationId?: string | null;
  traceId?: string | null;
  assistantMessageId?: string | null;
  historyCount?: number;
  memoryCount?: number;
  observationCount?: number;
  diagnosticReportCount?: number;
  toolCount?: number;
  evidenceCardCount?: number;
  mandatoryEvidenceCount?: number;
  missingTopics?: string[];
}

export type AgentFeedbackRating =
  | 'helpful'
  | 'needs_fix'
  | 'hard_to_understand'
  | 'too_verbose'
  | 'not_professional_enough'
  | 'not_actionable'
  | 'evidence_insufficient';

export type AgentRewriteTarget = 'simplify' | 'expand_evidence' | 'action_checklist';
export type AgentRememberScope = 'this_turn_only' | 'remember' | 'do_not_remember';

export interface AgentFeedback {
  rating: AgentFeedbackRating;
  category?: string | null;
  comment?: string | null;
  submittedAt?: string | null;
}

export interface AgentRewriteResponse {
  messageId: string;
  target: AgentRewriteTarget;
  content: string;
  metadata?: Record<string, unknown>;
  profileUpdated?: boolean;
}

export interface AgentProfileMemory {
  id: number;
  memoryType: string;
  key: string;
  label: string;
  summary: string;
  confidence: number;
  source?: string | null;
  sensitivity?: string | null;
  protected: boolean;
  userVisible: boolean;
  expiresAt?: string | null;
}

export interface AgentProfileMemoryList {
  learningEnabled: boolean;
  memories: AgentProfileMemory[];
}

export interface KnowledgePreferenceRequest {
  eventType: 'topic_interest' | 'listening_feedback';
  topicTitle?: string;
  topicKey?: string;
  selected?: boolean;
  reason?: string;
  feedback?: string;
  sourceTitle?: string;
  sourceId?: string;
  courseId?: string;
  lessonDay?: number;
}

export interface KnowledgePreferenceResponse {
  learningEnabled: boolean;
  profileUpdated: boolean;
  knowledgeLearningPreference: Record<string, unknown>;
}

export interface AgentRunMeta {
  quickPrompts?: string[];
  agent?: AgentRunStats;
  evidenceCards?: AgentEvidenceCard[];
  feedback?: AgentFeedback;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'ai' | 'assistant';
  text: string;
  time: string;
  createdAt?: string;
  sessionId?: string;
  status?: string | ThinkingProgress;
  attachments?: Attachment[];
  agentMeta?: AgentRunMeta;
}

export interface QuickPrompt {
  id: string;
  title: string;
  promptText: string;
  category: string;
}

/** 最近会话预览（GET /chat/latest） */
export interface LatestSession {
  id: string;
  title: string;
  lastMessage: string;
  lastMessageRole: string;
  lastMessageAt: string | null;
  status: string;
  createdAt: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

function inferMediaKind(mimeType: string, fileName: string): Attachment['mediaKind'] {
  const loweredMime = mimeType.toLowerCase();
  const loweredName = fileName.toLowerCase();
  if (loweredMime.includes('pdf') || loweredName.endsWith('.pdf')) return 'pdf';
  if (loweredMime.startsWith('image/')) return 'image';
  return 'file';
}

function toAttachmentPayload(attachments?: Attachment[]) {
  if (!attachments?.length) return undefined;
  return attachments.map((attachment, index) => ({
    attachmentId: attachment.attachmentId,
    fileOrder: attachment.fileOrder ?? index,
    groupId: attachment.groupId,
  }));
}

function numberField(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function stringField(value: unknown): string | null | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function normalizeFeedback(value: unknown): AgentFeedback | undefined {
  if (!isRecord(value)) return undefined;
  const rating = stringField(value.rating);
  if (
    rating !== 'helpful' &&
    rating !== 'needs_fix' &&
    rating !== 'hard_to_understand' &&
    rating !== 'too_verbose' &&
    rating !== 'not_professional_enough' &&
    rating !== 'not_actionable' &&
    rating !== 'evidence_insufficient'
  ) return undefined;
  return {
    rating,
    category: stringField(value.category),
    comment: stringField(value.comment),
    submittedAt: stringField(value.submittedAt) ?? stringField(value.submitted_at),
  };
}

export function normalizeAgentRunMeta(value: unknown): AgentRunMeta | undefined {
  if (!isRecord(value)) return undefined;

  const quickPrompts = stringArray(value.quickPrompts);
  const agentRaw = isRecord(value.agent) ? value.agent : undefined;
  const evidenceRaw = Array.isArray(value.evidenceCards) ? value.evidenceCards : [];
  const feedback = normalizeFeedback(value.feedback);

  const agent: AgentRunStats | undefined = agentRaw ? {
    route: stringField(agentRaw.route),
    invocationId: stringField(agentRaw.invocationId) ?? stringField(agentRaw.invocation_id),
    traceId: stringField(agentRaw.traceId),
    assistantMessageId: stringField(agentRaw.assistantMessageId) ?? stringField(agentRaw.messageId),
    historyCount: numberField(agentRaw.historyCount),
    memoryCount: numberField(agentRaw.memoryCount),
    observationCount: numberField(agentRaw.observationCount),
    diagnosticReportCount: numberField(agentRaw.diagnosticReportCount),
    toolCount: numberField(agentRaw.toolCount),
    evidenceCardCount: numberField(agentRaw.evidenceCardCount),
    mandatoryEvidenceCount: numberField(agentRaw.mandatoryEvidenceCount),
    missingTopics: stringArray(agentRaw.missingTopics),
  } : undefined;

  const evidenceCards = evidenceRaw
    .filter(isRecord)
    .map((card): AgentEvidenceCard => ({
      kind: stringField(card.kind),
      title: stringField(card.title),
      summary: stringField(card.summary),
      date: stringField(card.date),
      source: stringField(card.source),
      referenceRange: stringField(card.referenceRange),
      priority: Boolean(card.priority),
    }))
    .filter((card) => card.title || card.summary);

  if (!quickPrompts.length && !agent && !evidenceCards.length && !feedback) return undefined;
  return {
    ...(quickPrompts.length ? { quickPrompts } : {}),
    ...(agent ? { agent } : {}),
    ...(evidenceCards.length ? { evidenceCards } : {}),
    ...(feedback ? { feedback } : {}),
  };
}

function readAgentMeta(metadata: unknown): AgentRunMeta | undefined {
  if (!isRecord(metadata)) return undefined;
  const runMeta = (
    normalizeAgentRunMeta(metadata.agentcore_meta) ??
    normalizeAgentRunMeta(metadata.agentcoreMeta) ??
    normalizeAgentRunMeta(metadata.meta)
  );
  const feedback = normalizeFeedback(metadata.feedback);
  if (!feedback) return runMeta;
  return { ...(runMeta ?? {}), feedback };
}

// ── API 调用 ──

export const chatApi = {
  /** 创建新聊天会话 */
  async createSession(title?: string, metadata?: Record<string, unknown>): Promise<ChatSession> {
    return http.post<ChatSession>('/chat/sessions', { title, metadata });
  },

  /** 获取会话列表 */
  async getSessions(): Promise<ChatSession[]> {
    const res = await http.get<{ list: ChatSession[] }>('/chat/sessions');
    return res.list ?? [];
  },

  /** 获取最近一条会话预览 */
  async getLatestSession(): Promise<{ session: LatestSession | null }> {
    return http.get<{ session: LatestSession | null }>('/chat/latest');
  },

  /** 获取会话历史消息 */
  async getMessages(sessionId: string): Promise<ChatMessage[]> {
    const res = await http.get<{ list: any[] }>(`/chat/sessions/${encodeURIComponent(sessionId)}/messages`);
    return (res.list ?? []).map((msg) => {
      const createdAt = typeof msg.createdAt === 'string' ? msg.createdAt : undefined;
      const metaAttachments = msg.metadata?.attachments;
      const agentMeta = readAgentMeta(msg.metadata);
      const attachments: Attachment[] | undefined = metaAttachments
        ? metaAttachments.map((a: any) => ({
            attachmentId: a.attachmentId || a.attachment_id,
            url: a.url || a.ossUrl,
            mimeType: a.mimeType || a.mime_type || 'application/octet-stream',
            fileName: a.fileName || a.file_name || '附件',
            sizeBytes: a.sizeBytes || a.size_bytes,
            mediaKind: a.mediaKind || a.media_kind,
            uploadStatus: a.uploadStatus || a.upload_status,
            dedupeKey: a.dedupeKey || a.dedupe_key,
            groupId: a.groupId || a.group_id,
            fileOrder: a.fileOrder ?? a.file_order,
          }))
        : undefined;
      return {
        id: msg.id,
        role: msg.role === 'assistant' ? 'ai' : msg.role,
        text: msg.content || '',
        time: createdAt ? new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '刚刚',
        createdAt,
        sessionId: msg.sessionId || sessionId,
        attachments,
        agentMeta,
      };
    });
  },

  /** 上传聊天附件到 OSS（轻量，不创建 report task） */
  async uploadAttachment(file: File): Promise<Attachment> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('originalFileName', file.name);

    const token = useSessionStore.getState().token;
    const res = await fetch(`${API_BASE}/chat/attachments`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: '上传失败' }));
      throw new Error(err.error || `上传失败 (${res.status})`);
    }

    const data = await res.json();
    const payload = data.data ?? data;
    const mimeType = payload.mimeType || payload.mime_type || file.type;
    return {
      attachmentId: payload.attachmentId || payload.attachment_id || `local-${Date.now()}`,
      url: payload.url,
      mimeType,
      fileName: payload.fileName || payload.file_name || file.name,
      sizeBytes: payload.sizeBytes || payload.size_bytes || file.size,
      mediaKind: payload.mediaKind || payload.media_kind || inferMediaKind(mimeType, file.name),
      uploadStatus: payload.uploadStatus || payload.upload_status || 'uploaded',
      dedupeKey: payload.dedupeKey || payload.dedupe_key,
    };
  },

  /** 发送消息并获取 SSE 流式回复（推荐：自动加载历史+健康档案注入） */
  async *sendMessage(sessionId: string, content: string, attachments?: Attachment[], signal?: AbortSignal): AsyncGenerator<SSEEvent> {
    yield* createSSEStream(`/chat/sessions/${encodeURIComponent(sessionId)}/messages/stream`, {
      method: 'POST',
      body: { content, attachments: toAttachmentPayload(attachments) },
      signal,
      getToken: () => useSessionStore.getState().token,
    });
  },

  /** 发送消息（非流式） */
  async sendMessageSync(sessionId: string, content: string) {
    return http.post(`/chat/sessions/${encodeURIComponent(sessionId)}/messages`, { content });
  },

  /** 删除会话 */
  async deleteSession(sessionId: string) {
    return http.delete(`/chat/sessions/${encodeURIComponent(sessionId)}`);
  },

  /** 标记 AI 回复反馈 */
  async submitFeedback(
    sessionId: string,
    messageId: string,
    rating: AgentFeedbackRating,
    options?: {
      category?: string;
      comment?: string;
      rememberScope?: AgentRememberScope;
    },
  ) {
    return http.post<{
      messageId: string;
      feedback: AgentFeedback;
      profileUpdated?: boolean;
    }>(
      `/chat/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/feedback`,
      {
        rating,
        category: options?.category,
        comment: options?.comment,
        rememberScope: options?.rememberScope,
      },
    );
  },

  /** 以同一医学核心改写 AI 回复 */
  async rewriteMessage(
    sessionId: string,
    messageId: string,
    target: AgentRewriteTarget,
    rememberScope: AgentRememberScope = 'remember',
  ) {
    return http.post<AgentRewriteResponse>(
      `/chat/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/rewrite`,
      { target, rememberScope },
    );
  },

  /** 获取 AI 对用户的可见画像记忆 */
  async getProfileMemories(): Promise<AgentProfileMemoryList> {
    return http.get<AgentProfileMemoryList>('/chat/profile/memories');
  },

  /** 更新 AI 学习开关 */
  async updateProfilePrivacy(learningEnabled: boolean): Promise<{ learningEnabled: boolean }> {
    return http.post<{ learningEnabled: boolean }>('/chat/profile/memories/privacy', { learningEnabled });
  },

  /** 写入真知学习偏好 */
  async updateKnowledgePreference(payload: KnowledgePreferenceRequest): Promise<KnowledgePreferenceResponse> {
    if (shouldUseMock('chat')) {
      return {
        learningEnabled: true,
        profileUpdated: Boolean(payload.selected ?? payload.feedback),
        knowledgeLearningPreference: {
          topicTitle: payload.topicTitle,
          topicKey: payload.topicKey,
          selected: payload.selected,
          reason: payload.reason,
        },
      };
    }
    return http.post<KnowledgePreferenceResponse>('/chat/profile/memories/knowledge-preference', payload);
  },

  /** 删除单条 AI 画像记忆 */
  async deleteProfileMemory(memoryType: string, key: string): Promise<{ deleted: boolean }> {
    return http.delete<{ deleted: boolean }>(
      `/chat/profile/memories/${encodeURIComponent(memoryType)}/${encodeURIComponent(key)}`,
    );
  },

  /** 快捷提问模板 */
  async getQuickPrompts(sessionId?: string): Promise<QuickPrompt[]> {
    const url = sessionId
      ? `/chat/quick-prompts?session_id=${encodeURIComponent(sessionId)}`
      : '/chat/quick-prompts';
    const res = await http.get<{ prompts: QuickPrompt[] }>(url);
    return res.prompts ?? [];
  },
};
